import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api, setToken, getToken } from "@/lib/api";
import { useSimulation } from "@/hooks/useSimulation";
import HudHeader from "@/components/HudHeader";
import CityWorld from "@/components/CityWorld";
import CommandBar from "@/components/CommandBar";
import HireAgentModal from "@/components/HireAgentModal";
import AgentDrawer from "@/components/AgentDrawer";
import ActivityFeed from "@/components/ActivityFeed";
import DepartmentsPanel from "@/components/DepartmentsPanel";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import CollabModal from "@/components/CollabModal";
import ScoreboardPanel from "@/components/ScoreboardPanel";
import DeliverablesPanel from "@/components/DeliverablesPanel";
import ApprovalsPanel from "@/components/ApprovalsPanel";
import AutomationsPanel from "@/components/AutomationsPanel";
import MarketingStudioPanel from "@/components/MarketingStudioPanel";
import LifeInbox from "@/components/LifeInbox";
import MessageCenter from "@/components/MessageCenter";
import EmailClient from "@/components/EmailClient";
import DomainStore from "@/components/DomainStore";
import BillingStore from "@/components/BillingStore";
import LeadFinder from "@/components/LeadFinder";
import AIBoss from "@/components/AIBoss";
import IMessageConnect from "@/components/iMessageConnect";
import AuthPage from "@/components/AuthPage";
import UpgradeModal from "@/components/UpgradeModal";
import WalkMode from "@/components/WalkMode";
import CommandDock from "@/components/CommandDock";
import TelegramGroups from "@/components/TelegramGroups";
import { MusicProvider, useMusic } from "@/components/MusicProvider";
import BriefingPopup from "@/components/BriefingPopup";
import SharePage from "@/components/SharePage";
import { Loader2 } from "lucide-react";

function App() {
  const [authUser, setAuthUser] = useState(null); // null=checking, false=unauth, obj=user
  const [departments, setDepartments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleStatus, setGoogleStatus] = useState(null);

  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [hireOpen, setHireOpen] = useState(false);
  const [presetDept, setPresetDept] = useState(null);
  const [collabOpen, setCollabOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [walkMode, setWalkMode] = useState(false);
  const [mode, setMode] = useState("world");
  const [studioAgentId, setStudioAgentId] = useState(null);
  const [imsgOpen, setImsgOpen] = useState(false);
  const worldRef = useRef(null);

  useEffect(() => {
    const open = () => setImsgOpen(true);
    window.addEventListener("open-imessage", open);
    return () => window.removeEventListener("open-imessage", open);
  }, []);

  const deptById = useMemo(() => {
    const m = {};
    departments.forEach((d) => (m[d.id] = d));
    return m;
  }, [departments]);

  const { live, feed, paused, setPaused, speed, setSpeed, injectTask } = useSimulation(agents, deptById);

  const refreshUser = useCallback(() => { api.me().then(setAuthUser).catch(() => {}); }, []);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.bootstrap();
      setDepartments(data.departments || []);
      setAgents(data.agents || []);
      setIntegrations(data.integrations || []);
      setActivity(data.activity || []);
      setAuthUser(data.user);
      api.googleStatus().then(setGoogleStatus).catch(() => {});
    } catch {
      setToken(null);
      setAuthUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Boot: handle URL params (google identity login, gmail connect return, payment success), then auth
  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;

    const clean = () => window.history.replaceState({}, "", "/");

    (async () => {
      if (hash.includes("session_id=")) {
        const sid = hash.split("session_id=")[1].split("&")[0];
        try {
          const res = await api.googleSession(sid);
          setToken(res.token);
          toast.success("Signed in with Google");
        } catch { toast.error("Google sign-in failed"); }
        clean();
      } else if (params.get("google") === "connected") {
        toast.success("Google account connected");
        clean();
      } else if (params.get("google") === "error") {
        toast.error("Google connection failed");
        clean();
      } else if (path.startsWith("/payment/success")) {
        const sid = params.get("session_id");
        if (sid) {
          toast.loading("Confirming payment...", { id: "pay" });
          for (let i = 0; i < 6; i++) {
            try {
              const st = await api.paymentStatus(sid);
              if (st.payment_status === "paid") { toast.success("Payment confirmed — credits added!", { id: "pay" }); refreshUser(); break; }
            } catch {}
            await new Promise((r) => setTimeout(r, 1500));
          }
          toast.dismiss("pay");
        }
        clean();
      } else if (path.startsWith("/payment/cancel")) {
        toast("Checkout cancelled");
        clean();
      }

      if (getToken()) await loadWorkspace();
      else setAuthUser(false);
    })();
  }, [loadWorkspace]);

  useEffect(() => {
    const h = () => setUpgradeOpen(true);
    window.addEventListener("lifeos:limit", h);
    const unauth = () => { setToken(null); setAuthUser(false); setDepartments([]); setAgents([]); setIntegrations([]); setActivity([]); };
    window.addEventListener("lifeos:unauthorized", unauth);
    return () => { window.removeEventListener("lifeos:limit", h); window.removeEventListener("lifeos:unauthorized", unauth); };
  }, []);

  const mergedFeed = useMemo(() => {
    const all = [...activity, ...feed];
    all.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return all.slice(-80);
  }, [activity, feed]);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) || null;

  const focusAgent = useCallback((agent) => {
    setSelectedAgentId(agent.id);
    const dept = deptById[agent.department_id];
    if (dept && worldRef.current) worldRef.current.focusRoom(dept);
  }, [deptById]);

  const focusRoom = useCallback((dept) => { if (worldRef.current) worldRef.current.focusRoom(dept); }, []);

  const handleHire = async (payload) => {
    try {
      const agent = await api.hireAgent(payload);
      setAgents((p) => [...p, agent]);
      setActivity((p) => [...p, { id: agent.id + "-hire", agent_name: agent.name, department_code: deptById[agent.department_id]?.code, message: `${agent.name} was hired as ${agent.role}`, level: "success", created_at: new Date().toISOString() }]);
      toast.success(`${agent.name} deployed to the HQ`);
      setHireOpen(false);
      setTimeout(() => focusAgent(agent), 200);
    } catch { toast.error("Could not hire agent"); }
  };

  const handleRunTask = async (agentId, instruction) => {
    const task = await api.runTask(agentId, instruction);
    injectTask(agentId, task.steps, task.result);
    refreshUser();
    return task;
  };

  const handleUpdateAgent = async (id, payload) => {
    const updated = await api.updateAgent(id, payload);
    setAgents((p) => p.map((a) => (a.id === id ? updated : a)));
    return updated;
  };
  const handleDeleteAgent = async (id) => {
    await api.deleteAgent(id);
    setAgents((p) => p.filter((a) => a.id !== id));
    setSelectedAgentId(null);
    toast("Agent decommissioned");
  };

  const handleCreateDept = async (payload) => { const d = await api.createDepartment(payload); setDepartments((p) => [...p, d]); toast.success(`${d.name} room constructed`); };
  const handleUpdateDept = async (id, payload) => { const d = await api.updateDepartment(id, payload); setDepartments((p) => p.map((x) => (x.id === id ? d : x))); };
  const handleDeleteDept = async (id) => { await api.deleteDepartment(id); setDepartments((p) => p.filter((x) => x.id !== id)); setAgents((p) => p.filter((a) => a.department_id !== id)); toast("Room demolished"); };
  const handleToggleIntegration = async (id, connected) => { const u = await api.updateIntegration(id, connected); setIntegrations((p) => p.map((i) => (i.id === id ? u : i))); };

  const openHireForDept = (dept) => { setPresetDept(dept ? dept.id : null); setHireOpen(true); };

  const handleCollab = async (agentIds, objective) => {
    const collab = await api.startCollab(agentIds, objective);
    const meetingDept = departments.find((d) => d.name === "Meeting Room");
    agentIds.forEach((id) => injectTask(id, ["Entering Meeting Room...", "Sharing updates...", "Aligning on plan...", "Finalizing decisions..."], "Collaboration synced."));
    setActivity((p) => [...p, { id: collab.id + "-act", agent_name: "Meeting Room", department_code: "CONF", message: `${agentIds.length} agents synced on: ${objective.slice(0, 55)}`, level: "warn", created_at: new Date().toISOString() }]);
    if (meetingDept && worldRef.current) setTimeout(() => worldRef.current.focusRoom(meetingDept), 200);
    refreshUser();
    return collab;
  };

  const handleConnectGoogle = async () => {
    try {
      const { url } = await api.gmailLogin();
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google is not configured yet");
    }
  };

  const handleLogout = () => {
    setToken(null);
    setAuthUser(false);
    setDepartments([]); setAgents([]); setIntegrations([]); setActivity([]);
    toast("Signed out");
  };

  // ---- Render gates ----
  if (window.location.pathname.startsWith("/s/")) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <SharePage shareId={window.location.pathname.split("/s/")[1]} />
      </>
    );
  }
  if (authUser === null) {
    return (
      <div className="min-h-screen hq-grid grid place-items-center text-cyan-300">
        <Toaster theme="dark" position="top-center" richColors />
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }
  if (authUser === false) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <AuthPage onAuth={(u) => { setAuthUser(u); loadWorkspace(); }} />
      </>
    );
  }

  const activeCount = Object.values(live).filter((s) => s.phase === "working" || s.phase === "walking").length;
  const connectedCount = integrations.filter((i) => i.connected).length;

  return (
    <MusicProvider>
    <div className="App min-h-screen text-zinc-100 select-none">
      <Toaster theme="dark" position="top-center" richColors />
      <CityMusicSync active={mode === "world" && !walkMode} />

      <HudHeader
        agentCount={agents.length}
        activeCount={activeCount}
        connectedCount={connectedCount}
        feedCount={mergedFeed.length}
        mode={mode}
        setMode={setMode}
        onHire={() => openHireForDept(null)}
        user={authUser}
        onUpgrade={() => setUpgradeOpen(true)}
        onLogout={handleLogout}
      />

      <main className="fixed inset-0 top-16">
        <CityWorld ref={worldRef} departments={departments} agents={agents} live={live} deptById={deptById} loading={loading} onAgentClick={focusAgent} onRoomHire={openHireForDept} onBoss={() => setMode("boss")} paused={paused} speed={speed} active={mode === "world"} walking={walkMode} onWalkRoom={(d) => { setWalkMode(false); setTimeout(() => focusRoom(d), 160); }} onAtmRedeemed={refreshUser} />

        {mode === "world" && <ActivityFeed feed={mergedFeed} floating onClose={null} />}

        {mode === "logs" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm anim-slidein">
            <ActivityFeed feed={mergedFeed} full onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "departments" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <DepartmentsPanel departments={departments} agents={agents} live={live} onCreate={handleCreateDept} onUpdate={handleUpdateDept} onDelete={handleDeleteDept} onHire={openHireForDept} onFocus={(d) => { setMode("world"); setTimeout(() => focusRoom(d), 150); }} onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "connections" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <IntegrationsPanel integrations={integrations} onToggle={handleToggleIntegration} onClose={() => setMode("world")} googleStatus={googleStatus} onConnectGoogle={handleConnectGoogle} onGoogleConfigured={() => api.googleStatus().then(setGoogleStatus)} />
          </div>
        )}

        {mode === "groups" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <TelegramGroups onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "deliverables" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <DeliverablesPanel onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "approvals" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <ApprovalsPanel onClose={() => setMode("world")} googleConnected={googleStatus?.connected} />
          </div>
        )}

        {mode === "boss" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <AIBoss onClose={() => setMode("world")} onGoConnections={() => setMode("connections")} />
          </div>
        )}

        {mode === "life" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <LifeInbox onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "messages" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <MessageCenter onClose={() => setMode("world")} onOpenPerson={() => setMode("life")} onTopUp={() => setMode("billing")} />
          </div>
        )}

        {mode === "email" && (
          <div className="absolute inset-0 z-30 bg-[#05070b] anim-slidein">
            <EmailClient onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "store" && (
          <div className="absolute inset-0 z-30 bg-[#05070b] overflow-y-auto thin-scroll anim-slidein">
            <DomainStore onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "billing" && (
          <div className="absolute inset-0 z-30 bg-[#05070b] overflow-y-auto thin-scroll anim-slidein">
            <BillingStore onClose={() => setMode("world")} onRefreshUser={refreshUser} />
          </div>
        )}

        {mode === "leads" && (
          <div className="absolute inset-0 z-30 bg-[#05070b] overflow-y-auto thin-scroll anim-slidein">
            <LeadFinder onClose={() => setMode("world")} onRefreshUser={refreshUser} />
          </div>
        )}

        {mode === "marketing" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <MarketingStudioPanel agents={agents} initialAgentId={studioAgentId} onClose={() => setMode("world")} onRefreshUser={refreshUser} />
          </div>
        )}

        {mode === "automations" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <AutomationsPanel onClose={() => setMode("world")} />
          </div>
        )}

        {mode === "scoreboard" && (
          <div className="absolute inset-0 z-30 bg-[#05070b]/95 backdrop-blur-sm overflow-y-auto thin-scroll anim-slidein">
            <ScoreboardPanel onClose={() => setMode("world")} onFocusAgent={(id) => { const a = agents.find((x) => x.id === id); if (a) { setMode("world"); setTimeout(() => focusAgent(a), 150); } }} />
          </div>
        )}

        {mode === "world" && (
          <CommandBar paused={paused} setPaused={setPaused} speed={speed} setSpeed={setSpeed} onZoomIn={() => worldRef.current?.zoomIn()} onZoomOut={() => worldRef.current?.zoomOut()} onRecenter={() => worldRef.current?.recenter()} onCollab={() => setCollabOpen(true)} onWalk={() => setWalkMode(true)} />
        )}

        {walkMode && (
          <WalkMode worldRef={worldRef} onExit={() => setWalkMode(false)} />
        )}
      </main>

      <CollabModal open={collabOpen} onOpenChange={setCollabOpen} agents={agents} deptById={deptById} onRun={handleCollab} />
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} currentPlan={authUser.plan} />

      <AgentDrawer agent={selectedAgent} dept={selectedAgent ? deptById[selectedAgent.department_id] : null} departments={departments} live={selectedAgent ? live[selectedAgent.id] : null} onClose={() => setSelectedAgentId(null)} onRunTask={handleRunTask} onUpdate={handleUpdateAgent} onDelete={handleDeleteAgent} googleConnected={googleStatus?.connected} onDesign={(id) => { setStudioAgentId(id); setSelectedAgentId(null); setMode("marketing"); }} />

      <CommandDock page={mode} onNavigate={setMode} onRefresh={loadWorkspace} onFocusAgent={(id) => { const a = agents.find((x) => x.id === id); setMode("world"); if (a) setTimeout(() => focusAgent(a), 200); }} />
      <BriefingPopup onOpenBoss={() => setMode("boss")} />

      <HireAgentModal open={hireOpen} onOpenChange={setHireOpen} departments={departments} presetDept={presetDept} onHire={handleHire} />
      {imsgOpen && <IMessageConnect onClose={() => setImsgOpen(false)} onScanned={loadWorkspace} />}
    </div>
    </MusicProvider>
  );
}

function CityMusicSync({ active }) {
  const music = useMusic();
  useEffect(() => { music?.setCityActive(active); }, [active, music]);
  return null;
}

export default App;
